import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  BarChart2,
  MessageSquare,
  Calendar,
  Send,
  Users,
  Settings,
  Cloud,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Repeat,
  Bot,
  GraduationCap,
  Zap,
  Webhook,
  Bell,
  Search,
  Target,
  Sparkles,
  CreditCard
} from 'lucide-react';

interface SideNavProps {
  isCollapsed: boolean;
  onToggleCollapse?: () => void;
}

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/performance', icon: BarChart2, label: 'Performance' },
  { path: '/messaging', icon: MessageSquare, label: 'Messaging' },
  { path: '/calendar', icon: Calendar, label: 'Calendar & Scheduling' },
  { path: '/contacts', icon: Users, label: 'Contacts' },
  { path: '/library', icon: FolderOpen, label: 'Content Vault' },
  { path: '/repurpose', icon: Repeat, label: 'Content Repurposing' },
  { path: '/creator-assistant', icon: Bot, label: 'Creator Assistant' },
  { path: '/multi-platform-composer', icon: Send, label: 'Multi-Platform Composer' },
  { path: '/ai-tools', icon: Sparkles, label: 'AI Tools' },
  { path: '/campaign-generator', icon: Target, label: 'Campaign Generator' },
  { path: '/learning-hub', icon: GraduationCap, label: 'Learning Hub' },
  { path: '/subscription', icon: CreditCard, label: 'Subscription' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export const SideNav: React.FC<SideNavProps> = ({ isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showNewFeatures, setShowNewFeatures] = useState(false);

  const handleNewFeaturesClick = () => {
    navigate('/docs/new-features');
    setShowNewFeatures(false);
  };

  return (
    <div className="flex flex-col h-full relative overflow-y-auto overflow-x-hidden max-w-64">
      <div className="flex-none pt-5 px-4 mb-8">
        <Link
          to="/dashboard"
          className={`flex items-center space-x-3 ${isCollapsed ? 'justify-center' : ''}`}
        >
          <div className="relative">
            <Cloud className="h-8 w-8 text-indigo-600" />
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
            </span>
          </div>
          {!isCollapsed && (
            <div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                Serge
              </span>
              <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                Pro Dashboard
              </div>
            </div>
          )}
        </Link>
      </div>

      {!isCollapsed && (
        <div className="flex-none px-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              className="w-full pl-9 pr-4 py-2 bg-gray-100 dark:bg-dark-700 border-0 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            />
          </div>
        </div>
      )}

      <div className="flex-none px-4 mb-4">
        <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          {isCollapsed ? 'Menu' : 'Main Menu'}
        </div>
      </div>

      {/* Scrollable navigation area - hide scrollbar but keep functionality */}
      <div className="flex-1 px-2 space-y-1 no-scrollbar overflow-x-hidden">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`
                flex items-center px-3 py-3 mb-1 transition-colors relative rounded-lg
                ${isCollapsed ? 'justify-center' : 'space-x-3'}
                ${isActive
                  ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-dark-700'
                }
              `}
            >
              <item.icon className="h-5 w-5 relative z-10" />
              {!isCollapsed && (
                <span className="text-sm font-medium relative z-10 truncate">{item.label}</span>
              )}
            </Link>
          );
        })}
      </div>

      {!isCollapsed && (
        <div className="flex-none p-4 mt-auto overflow-x-hidden">
          <button
            onClick={handleNewFeaturesClick}
            className="w-full bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3 border border-indigo-100 dark:border-indigo-800/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors overflow-hidden"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-800/30 rounded-lg">
                <Bell className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  New Features
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Check out the latest updates
                </p>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Collapse Button - Fixed positioning to ensure it's fully visible */}
      {onToggleCollapse && (
        <div className="fixed top-1/2 right-0 z-50 transform translate-x-1/2">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onToggleCollapse}
            className="flex items-center justify-center w-10 h-10 bg-white dark:bg-dark-800 rounded-full shadow-lg border border-gray-200 dark:border-dark-700 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-5 w-5" />
            )}
          </motion.button>
        </div>
      )}
    </div>
  );
};